/* Yleiset widgets: quick actions, search, What's New, plan, team, note. */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BoltIcon from '@mui/icons-material/Bolt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CampaignIcon from '@mui/icons-material/Campaign';
import DescriptionIcon from '@mui/icons-material/Description';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import GroupIcon from '@mui/icons-material/Group';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

import { dataSources } from '../dataSources';
import { useDashboardActions } from '../dashboardActions';
import { useWidgetFrame } from '../frameContext';
import BreakdownTile from '../templates/BreakdownTile';
import ButtonsRow from '../templates/ButtonsRow';
import KpiTile from '../templates/KpiTile';
import ListBody from '../templates/ListBody';
import NoteBody from '../templates/NoteBody';
import SearchBody from '../templates/SearchBody';
import { fmtDay, fmtEur0, fmtNumber } from '../templates/format';
import { listOf, useHeader, useOrg, useSource } from '../widgetHooks';

import { can } from '../../../utils/access';
import { isQuoteMachineLaunchPeriod } from '../../quote-machine/launch';
/** Quick action catalogue: key -> { labelKey, icon, available(org), badgeKey?, badgeActive?() }; `available` follows the same keys as the routes. */
export const QUICK_ACTIONS = {
  new_wo: { labelKey: 'dashboard.quick.newWorkOrder', icon: AddIcon, available: (org) => can(org, 'work_orders.create') },
  new_quote: { labelKey: 'dashboard.quick.newQuote', icon: DescriptionIcon, available: (org) => can(org, 'work_orders.create') },
  new_invoice: { labelKey: 'dashboard.quick.newInvoice', icon: ReceiptIcon, available: (org) => can(org, 'invoices.manage') },
  quote_machine: { labelKey: 'dashboard.quick.quoteMachine', icon: BoltIcon, available: (org) => Boolean(org?.quote_machine_enabled) && can(org, 'work_orders.create'), badgeKey: 'dashboard.quick.newBadge', badgeActive: isQuoteMachineLaunchPeriod },
  ai_wo: { labelKey: 'dashboard.quick.aiWorkOrder', icon: AutoAwesomeIcon, available: (org) => Boolean(org?.ai_work_order_enabled) && can(org, 'work_orders.create') },
  counter_sale: { labelKey: 'dashboard.quick.counterSale', icon: PointOfSaleIcon, available: (org) => Boolean(org?.counter_sales_available) && can(org, 'parts_sales.manage') },
  new_lahete: { labelKey: 'dashboard.quick.newLahete', icon: ReceiptLongIcon, available: (org) => Boolean(org?.parts_sales_available) && can(org, 'parts_sales.manage') },
  open_calendar: { labelKey: 'dashboard.quick.openCalendar', icon: CalendarMonthIcon },
  open_inventory: { labelKey: 'dashboard.quick.openInventory', icon: WarehouseIcon, available: (org) => Boolean(org?.inventory_tracking_enabled) && can(org, 'menu.inventory') },
};
// Tarjousautomaatti is in the default set since its launch (16.10.2026); it is dropped by
// `available` for workshops without the entitlement, so nothing changes for them.
export const DEFAULT_QUICK_ACTIONS = ['new_wo', 'new_quote', 'new_invoice', 'quote_machine'];

export function availableQuickActions(org) {
  return Object.keys(QUICK_ACTIONS).filter((key) => !QUICK_ACTIONS[key].available || QUICK_ACTIONS[key].available(org));
}

export function QuickActionsWidget({ opts }) {
  const { t } = useTranslation();
  const org = useOrg();
  const { open } = useDashboardActions();
  const keys = (Array.isArray(opts?.actions) && opts.actions.length ? opts.actions : DEFAULT_QUICK_ACTIONS).filter((key) => QUICK_ACTIONS[key] && availableQuickActions(org).includes(key));
  const actions = keys.map((key) => {
    const action = QUICK_ACTIONS[key];
    const badge = action.badgeKey && (!action.badgeActive || action.badgeActive()) ? t(action.badgeKey) : null;
    return { key, label: t(action.labelKey), icon: action.icon, badge, onClick: () => open(key) };
  });
  // Right is the default: the old landing page had these buttons at the right edge of the
  // header and every workshop is used to finding them there.
  return <ButtonsRow actions={actions} align={opts?.align === 'left' ? 'left' : 'right'} />;
}

export function SearchWidget() {
  return <SearchBody />;
}

export function WhatsNewWidget() {
  const { t } = useTranslation();
  const { open } = useDashboardActions();
  const { data } = useSource(dataSources.whatsNew());
  const items = (data?.items || []).slice(0, 10);
  const unread = data?.unread_count ?? 0;
  useHeader({ chip: unread ? t('dashboard.chips.newCount', { count: unread }) : null, chipTone: 'accent', link: { labelKey: 'dashboard.actions.showAll', onClick: () => open('whatsnew') }, empty: Boolean(data) && items.length === 0 }, [data, items.length, unread]);
  const rows = items.map((item, index) => ({
    key: item.id || index,
    icon: index < unread ? FiberManualRecordIcon : CampaignIcon,
    iconColor: index < unread ? '#3182ce' : '#94a3b8',
    primary: item.title,
    secondary: fmtDay(item.published_at),
    chip: item.category ? t(`whatsnew.category.${item.category}`, { defaultValue: item.category }) : null,
    chipTone: 'accent',
    onClick: () => open('whatsnew'),
  }));
  return <ListBody rows={rows} onMore={() => open('whatsnew')} />;
}

export function PlanWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const info = useSource(dataSources.subscriptionInfo());
  const seats = useSource(dataSources.seats());
  const usage = useSource(dataSources.usage());
  const quota = useSource(dataSources.lookupQuota());
  const plan = info.data?.plan;
  useHeader({ link: { labelKey: 'dashboard.actions.openSettings', to: '/settings' } }, []);
  return (
    <BreakdownTile
      icon={WorkspacePremiumIcon}
      value={plan?.display_name || plan?.name || (info.data?.is_trial ? t('dashboard.chips.trial') : '–')}
      unit={info.data?.is_trial && info.data?.trial_days_remaining != null ? t('dashboard.chips.trialDays', { count: info.data.trial_days_remaining }) : ''}
      chips={[
        { n: `${fmtNumber(seats.data?.seats_total_used ?? info.data?.seats_used ?? 0)}/${seats.data?.is_unlimited ? '∞' : fmtNumber(seats.data?.seat_limit ?? info.data?.seat_limit ?? 0)}`, label: t('dashboard.chips.seats') },
        { n: `${fmtNumber(quota.data?.used_this_period ?? 0)}/${fmtNumber(quota.data?.included_lookups ?? 0)}`, label: t('dashboard.chips.vehicleLookups') },
      ]}
      rows={[{ icon: ReceiptIcon, label: t('dashboard.chips.usageThisMonth'), value: fmtEur0(usage.data?.estimated_charges?.total ?? 0) }]}
      onClick={() => navigate('/settings')}
    />
  );
}

export function TeamTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const employees = useSource(dataSources.employees());
  const invitations = useSource(dataSources.invitations());
  const active = listOf(employees.data).filter((employee) => employee.state === 'active').length;
  const pending = listOf(invitations.data).length;
  return <KpiTile icon={GroupIcon} value={active} label={t('dashboard.widgets.team.title')} sub={pending ? t('dashboard.chips.invitesPending', { count: pending }) : null} onClick={() => navigate('/employees')} />;
}

export function NoteWidget({ opts }) {
  const { t } = useTranslation();
  const frame = useWidgetFrame();
  const text = opts?.text || '';
  const onChange = useMemo(() => (next) => frame?.setOpts?.({ ...(opts || {}), text: next }), [frame, opts]);
  return <NoteBody value={text} onChange={onChange} placeholder={t('dashboard.empty.note')} />;
}
